/**
 * Service Bid Board listing kinds.
 *
 * RFP = proposals in a min–max range (guidance ceiling for quotes).
 * Funded bid = contractors race on price + on-site time.
 * Escrow for RFP / funded bid is funded AFTER the operator awards a bid
 * (award amount → Stripe Checkout / prepaid tank → operator_award hold).
 * customer_offer still escrows posted price at post (posted-price accept).
 */

export const LISTING_KIND_RFP = 'rfp';
export const LISTING_KIND_FUNDED_BID = 'funded_bid';
export const LISTING_KIND_CUSTOMER_OFFER = 'customer_offer';

export type ListingKind =
  | typeof LISTING_KIND_RFP
  | typeof LISTING_KIND_FUNDED_BID
  | typeof LISTING_KIND_CUSTOMER_OFFER;

export const BID_SITE_RESIDENTIAL = 'residential';
export const BID_SITE_COMMERCIAL = 'commercial';
export const BID_SITE_OILFIELD = 'oilfield';

export type BidSiteType =
  | typeof BID_SITE_RESIDENTIAL
  | typeof BID_SITE_COMMERCIAL
  | typeof BID_SITE_OILFIELD;

export const BID_SITE_TYPE_OPTIONS: ReadonlyArray<{
  id: BidSiteType;
  label: string;
  subtitle: string;
}> = [
  { id: BID_SITE_RESIDENTIAL, label: 'Residential', subtitle: 'House, ranch, or shop at a street address' },
  { id: BID_SITE_COMMERCIAL, label: 'Business / commercial', subtitle: 'Yard, warehouse, storefront, or shop' },
  { id: BID_SITE_OILFIELD, label: 'Oilfield', subtitle: 'Pad / lease / CTB — API or facility' },
];

export function normalizeBidSiteType(raw: unknown): BidSiteType {
  const value = String(raw || '').trim().toLowerCase();
  if (value === BID_SITE_RESIDENTIAL || value === 'home' || value === 'house') return BID_SITE_RESIDENTIAL;
  if (value === BID_SITE_COMMERCIAL || value === 'business' || value === 'commercial') return BID_SITE_COMMERCIAL;
  return BID_SITE_OILFIELD;
}

export function bidSiteTypeLabel(raw: unknown): string {
  const id = normalizeBidSiteType(raw);
  return BID_SITE_TYPE_OPTIONS.find((o) => o.id === id)?.label || 'Oilfield';
}

export function isOilfieldBidSite(raw: unknown): boolean {
  return normalizeBidSiteType(raw) === BID_SITE_OILFIELD;
}

/** Bids board flip: residential street jobs vs operator API / commercial sites. */
export type ListingBoardLane = 'operators' | 'residential';

export function listingBoardLane(input: {
  siteType?: unknown;
  apiNumber?: unknown;
  streetAddress?: unknown;
}): ListingBoardLane {
  const typed = String(input.siteType ?? '').trim();
  if (typed) {
    return normalizeBidSiteType(typed) === BID_SITE_RESIDENTIAL ? 'residential' : 'operators';
  }
  const api = String(input.apiNumber ?? '').replace(/\D/g, '');
  if (api.length >= 8) return 'operators';
  if (String(input.streetAddress ?? '').trim()) return 'residential';
  return 'operators';
}

export function listingMatchesBoardLane(
  input: {
    siteType?: unknown;
    apiNumber?: unknown;
    streetAddress?: unknown;
  },
  lane?: ListingBoardLane | null,
): boolean {
  if (!lane) return true;
  return listingBoardLane(input) === lane;
}

/** Service categories that make sense off a pad (house / shop / yard). */
export const STREET_SITE_CATEGORY_VALUES: ReadonlySet<string> = new Set([
  'welding',
  'mechanical',
  'electrical',
  'automation',
  'construction',
  'fencing',
  'crane',
  'heavyEquipment',
  'trucking',
  'hotShot',
  'flatbed',
  'equipmentRental',
  'siteServices',
  'portapotty',
  'trash',
  'security',
  'communications',
  'catering',
  'consulting',
  'labor',
  'generator',
  'pumpRepair',
  'inspection',
  'drone',
  'vacuum',
  'hydrovac',
  'tankCleaning',
  'other',
]);

export function categoriesForBidSite<T extends { value: string }>(
  siteType: unknown,
  all: ReadonlyArray<T>,
): T[] {
  if (isOilfieldBidSite(siteType)) return [...all];
  return all.filter((c) => STREET_SITE_CATEGORY_VALUES.has(c.value));
}

export function defaultCategoryForBidSite(
  siteType: unknown,
  current: string | null | undefined,
  all: ReadonlyArray<{ value: string }>,
): string {
  const allowed = categoriesForBidSite(siteType, all);
  const keep = String(current || '').trim();
  if (keep && allowed.some((c) => c.value === keep)) return keep;
  return allowed.find((c) => c.value === 'welding')?.value
    || allowed[0]?.value
    || 'other';
}

export function normalizeListingKind(raw: unknown): ListingKind {
  const value = String(raw || '').trim().toLowerCase();
  if (value === LISTING_KIND_FUNDED_BID || value === 'bid' || value === 'funded') {
    return LISTING_KIND_FUNDED_BID;
  }
  if (value === LISTING_KIND_CUSTOMER_OFFER || value === 'offer' || value === 'posted_price') {
    return LISTING_KIND_CUSTOMER_OFFER;
  }
  return LISTING_KIND_RFP;
}

export function isFundedBidListing(raw: unknown): boolean {
  return normalizeListingKind(raw) === LISTING_KIND_FUNDED_BID;
}

export function isCustomerOfferListing(raw: unknown): boolean {
  return normalizeListingKind(raw) === LISTING_KIND_CUSTOMER_OFFER;
}

export function isRfpListing(raw: unknown): boolean {
  return normalizeListingKind(raw) === LISTING_KIND_RFP;
}

/** RFP / Bid / Offer use escrow holds (award-time for RFP/Bid; post-time for offers). */
export function listingHoldsEscrowReserve(raw: unknown): boolean {
  const kind = normalizeListingKind(raw);
  return kind === LISTING_KIND_RFP
    || kind === LISTING_KIND_FUNDED_BID
    || kind === LISTING_KIND_CUSTOMER_OFFER;
}

export function isCustomerPosterProfile(raw: unknown): boolean {
  return String(raw || '').trim().toLowerCase() === 'customer';
}

export function listingKindLabel(raw: unknown): string {
  if (isCustomerOfferListing(raw)) return 'OFFER · posted price';
  return isFundedBidListing(raw) ? 'BID · funded' : 'RFP';
}

export function listingKindHint(raw: unknown): string {
  if (isCustomerOfferListing(raw)) {
    return 'Posted price — a driver or hauler accepts this dollar amount and completes the work. Not an auction.';
  }
  return isFundedBidListing(raw)
    ? 'Funded bid — listing goes live without holding money. After you award a contractor, that award amount is funded into escrow. Lowest bid that can hit the on-site time is what you pick from. Payout only after work complete + ops review (Accept ≠ payday).'
    : 'RFP — min and max you’ll accept (quote ceiling). Listing goes live without holding money. After you award, that award amount is funded into escrow. Payout only after work complete + ops review (Accept ≠ payday).';
}

export function isValidContactEmail(raw: unknown): boolean {
  const email = String(raw ?? '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

export function isValidContactPhone(raw: unknown): boolean {
  let digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return false;
  if (digits[0] === '0' || digits[0] === '1') return false;
  if (digits[3] === '0' || digits[3] === '1') return false;
  return true;
}

export function operatorBidContactIssue(input: {
  firstName?: unknown;
  lastName?: unknown;
  phone?: unknown;
  email?: unknown;
}): string | null {
  const first = String(input.firstName ?? '').trim();
  const last = String(input.lastName ?? '').trim();
  const phone = String(input.phone ?? '').trim();
  const email = String(input.email ?? '').trim();
  if (!first || !last) return 'First name and last name are required.';
  if (!isValidContactPhone(phone)) return 'Enter a valid 10-digit US phone number.';
  if (!isValidContactEmail(email)) return 'Enter a valid email address.';
  return null;
}

export function operatorBidContactComplete(input: {
  firstName?: unknown;
  lastName?: unknown;
  phone?: unknown;
  email?: unknown;
}): boolean {
  return operatorBidContactIssue(input) == null;
}

export function operatorBidContactName(first: unknown, last: unknown): string {
  return [String(first ?? '').trim(), String(last ?? '').trim()].filter(Boolean).join(' ');
}

export function normalizeWorkArea(raw: unknown): Array<{ lat: number; lng: number }> | null {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    try { list = JSON.parse(raw); } catch { return null; }
  }
  if (!Array.isArray(list) || list.length < 4) return null;
  const pts: Array<{ lat: number; lng: number }> = [];
  for (const row of list) {
    const lat = Number((row as { lat?: unknown })?.lat);
    const lng = Number((row as { lng?: unknown })?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    pts.push({ lat, lng });
  }
  return pts.length >= 4 ? pts : null;
}

export type AccessRouteWaypointType = 'start' | 'turn_left' | 'turn_right' | 'destination';

export type AccessRouteWaypoint = {
  type: AccessRouteWaypointType;
  lat: number;
  lng: number;
  name: string;
};

export type AccessRouteDirection = {
  instruction: string;
  distanceMeters?: number;
};

/** Finger-drawn caliche access path attached to an RFP. */
export type AccessRouteJson = {
  name: string;
  description?: string;
  polyline: Array<{ lat: number; lng: number }>;
  waypoints: AccessRouteWaypoint[];
  directions: AccessRouteDirection[];
  googleDirections?: AccessRouteDirection[];
  mapsUrl?: string;
  distanceMeters?: number;
  durationSeconds?: number;
};

const ACCESS_ROUTE_MAX_POLYLINE = 400;
const ACCESS_ROUTE_MAX_DIRECTIONS = 80;
const ACCESS_ROUTE_MAX_NAME = 120;
const ACCESS_ROUTE_MAX_DESCRIPTION = 500;

function accessRouteCoord(raw: unknown): { lat: number; lng: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as { lat?: unknown; lng?: unknown; latitude?: unknown; longitude?: unknown };
  const lat = Number(rec.lat ?? rec.latitude);
  const lng = Number(rec.lng ?? rec.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

function accessRouteWaypointType(raw: unknown): AccessRouteWaypointType {
  const value = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (value === 'turn_left' || value === 'left') return 'turn_left';
  if (value === 'turn_right' || value === 'right') return 'turn_right';
  if (value === 'destination' || value === 'end' || value === 'finish') return 'destination';
  return 'start';
}

function accessRouteDirections(raw: unknown): AccessRouteDirection[] {
  if (!Array.isArray(raw)) return [];
  const out: AccessRouteDirection[] = [];
  for (const row of raw) {
    if (typeof row === 'string') {
      const instruction = row.trim();
      if (instruction) out.push({ instruction });
      continue;
    }
    if (!row || typeof row !== 'object') continue;
    const rec = row as { instruction?: unknown; name?: unknown; distanceMeters?: unknown };
    const instruction = String(rec.instruction ?? rec.name ?? '').trim();
    if (!instruction) continue;
    const meters = Number(rec.distanceMeters);
    out.push({
      instruction: instruction.slice(0, 240),
      distanceMeters: Number.isFinite(meters) && meters > 0 ? meters : undefined,
    });
    if (out.length >= ACCESS_ROUTE_MAX_DIRECTIONS) break;
  }
  return out;
}

export function normalizeAccessRoute(raw: unknown): AccessRouteJson | null {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try { value = JSON.parse(trimmed); } catch { return null; }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const rec = value as Record<string, unknown>;
  const polyRaw = rec.polyline ?? rec.path ?? rec.points;
  if (!Array.isArray(polyRaw)) return null;
  const polyline: Array<{ lat: number; lng: number }> = [];
  for (const pt of polyRaw) {
    const coord = accessRouteCoord(pt);
    if (!coord) continue;
    polyline.push(coord);
    if (polyline.length >= ACCESS_ROUTE_MAX_POLYLINE) break;
  }
  if (polyline.length < 2) return null;

  const waypointsRaw = Array.isArray(rec.waypoints) ? rec.waypoints : [];
  const waypoints: AccessRouteWaypoint[] = [];
  for (const wp of waypointsRaw) {
    const coord = accessRouteCoord(wp);
    if (!coord) continue;
    const recWp = (wp && typeof wp === 'object') ? wp as Record<string, unknown> : {};
    waypoints.push({
      type: accessRouteWaypointType(recWp.type),
      lat: coord.lat,
      lng: coord.lng,
      name: String(recWp.name || recWp.instruction || '').trim().slice(0, 80) || accessRouteWaypointType(recWp.type),
    });
  }
  if (waypoints.length < 2) {
    waypoints.splice(0, waypoints.length,
      { type: 'start', lat: polyline[0].lat, lng: polyline[0].lng, name: 'Start' },
      {
        type: 'destination',
        lat: polyline[polyline.length - 1].lat,
        lng: polyline[polyline.length - 1].lng,
        name: 'Destination',
      },
    );
  }

  const directions = accessRouteDirections(rec.directions);
  const googleDirections = accessRouteDirections(rec.googleDirections);
  const name = String(rec.name || '').trim().slice(0, ACCESS_ROUTE_MAX_NAME) || 'Caliche access route';
  const description = String(rec.description || '').trim().slice(0, ACCESS_ROUTE_MAX_DESCRIPTION) || undefined;
  const mapsUrl = String(rec.mapsUrl || rec.maps_url || '').trim();
  const distanceMeters = Number(rec.distanceMeters ?? rec.distance_meters);
  const durationSeconds = Number(rec.durationSeconds ?? rec.duration_seconds);

  const out: AccessRouteJson = {
    name,
    polyline,
    waypoints,
    directions: directions.length
      ? directions
      : waypoints.map((wp) => ({ instruction: wp.name })),
  };
  if (description) out.description = description;
  if (googleDirections.length) out.googleDirections = googleDirections;
  if (mapsUrl.startsWith('https://www.google.com/maps/')) out.mapsUrl = mapsUrl;
  if (Number.isFinite(distanceMeters) && distanceMeters > 0) out.distanceMeters = distanceMeters;
  if (Number.isFinite(durationSeconds) && durationSeconds > 0) out.durationSeconds = durationSeconds;
  return out;
}

/** True when both bounds are set and min is above max. Empty / non-numeric is not an inversion. */
export function budgetMinExceedsMax(min: unknown, max: unknown): boolean {
  const minRaw = String(min ?? '').trim();
  const maxRaw = String(max ?? '').trim();
  if (!minRaw || !maxRaw) return false;
  const lo = Number(minRaw);
  const hi = Number(maxRaw);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return false;
  return lo > hi;
}

export function hasBidGps(lat: unknown, lng: unknown): boolean {
  if (lat == null || lng == null || lat === '' || lng === '') return false;
  const latitude = Number(lat);
  const longitude = Number(lng);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
    && !(latitude === 0 && longitude === 0);
}

export const AUCTION_DURATION_UNITS = [
  { id: 'seconds', label: 'Seconds', ms: 1000 },
  { id: 'minutes', label: 'Minutes', ms: 60_000 },
  { id: 'hours', label: 'Hours', ms: 3_600_000 },
  { id: 'days', label: 'Days', ms: 86_400_000 },
  { id: 'weeks', label: 'Weeks', ms: 604_800_000 },
  { id: 'months', label: 'Months', ms: 30 * 86_400_000 },
] as const;

export type AuctionDurationUnit = (typeof AUCTION_DURATION_UNITS)[number]['id'];

export function auctionClosesAtIso(
  amount: unknown,
  unit: string,
  fromMs = Date.now(),
): string {
  const n = Math.max(1, Math.floor(Number(amount) || 1));
  const row = AUCTION_DURATION_UNITS.find((u) => u.id === unit) || AUCTION_DURATION_UNITS[3];
  return new Date(fromMs + n * row.ms).toISOString();
}

/** Reserve in cents. Funded bids use budget max (dollars on the bid row). */
export function fundedBidReserveCents(budgetMax: unknown): number {
  const dollars = Number(budgetMax);
  if (!Number.isFinite(dollars) || dollars <= 0) return 0;
  return Math.round(dollars * 100);
}

/** Remaining ms until bids close. Negative means closed. */
export function bidCountdownRemainingMs(closesAt: string | Date | null | undefined, nowMs = Date.now()): number {
  if (!closesAt) return 0;
  const closeMs = closesAt instanceof Date ? closesAt.getTime() : new Date(closesAt).getTime();
  if (!Number.isFinite(closeMs)) return 0;
  return closeMs - nowMs;
}

export function listingRowTitle(title: unknown, site: unknown): string {
  const t = String(title || '').trim();
  const s = String(site || '').trim();
  if (!t) return s || 'RFP';
  if (!s) return t;
  const suffix = ` · ${s}`;
  if (t.toLowerCase().endsWith(suffix.toLowerCase())) {
    const stripped = t.slice(0, t.length - suffix.length).trim();
    return stripped || t;
  }
  return t;
}

export function listingRowShowsSite(title: string, site: unknown): boolean {
  const s = String(site || '').trim();
  if (!s) return false;
  return !title.toLowerCase().includes(s.toLowerCase());
}

export function formatListingCountdown(
  closesAt: string | Date | null | undefined,
  nowMs = Date.now(),
  closedLabel = 'Bidding closed',
  leftLabel = 'left',
  noCloseLabel = 'No close time',
): string {
  const remaining = bidCountdownRemainingMs(closesAt, nowMs);
  if (!closesAt) return noCloseLabel;
  if (remaining <= 0) return closedLabel;
  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days >= 2) return `${days}d ${hours}h ${leftLabel}`;
  if (days >= 1) return `${days}d ${hours}h ${minutes}m ${leftLabel}`;
  if (hours >= 1) return `${hours}h ${minutes}m ${leftLabel}`;
  if (minutes >= 1) return `${minutes}m ${String(seconds).padStart(2, '0')}s ${leftLabel}`;
  return `${seconds}s ${leftLabel}`;
}

export function formatBidCountdown(
  closesAt: string | Date | null | undefined,
  nowMs = Date.now(),
): string {
  return formatListingCountdown(closesAt, nowMs, 'Bidding closed');
}

export function formatRfpCountdown(
  closesAt: string | Date | null | undefined,
  nowMs = Date.now(),
): string {
  return formatListingCountdown(closesAt, nowMs, 'Quotes closed');
}

export function listingIsStillOpen(
  closesAt: string | Date | null | undefined,
  nowMs = Date.now(),
): boolean {
  if (!closesAt) return true;
  return bidCountdownRemainingMs(closesAt, nowMs) > 0;
}

export function formatOnSiteWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (!Number.isFinite(dt.getTime())) return String(iso);
  return dt.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatAvailableDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const raw = String(iso);
  const day = raw.length <= 10 ? raw : raw.slice(0, 10);
  const dt = new Date(`${day}T12:00:00`);
  if (!Number.isFinite(dt.getTime())) return raw;
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Calendar day (YYYY-MM-DD) in UTC. Date-only strings stay as written. */
export function listingDayKey(raw: string | Date | null | undefined): string | null {
  if (!raw) return null;
  if (raw instanceof Date) {
    if (!Number.isFinite(raw.getTime())) return null;
    return raw.toISOString().slice(0, 10);
  }
  const value = String(raw).trim();
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const dt = new Date(value);
  if (!Number.isFinite(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
}

/**
 * Contractor can make the operator on-site day.
 * Missing either date → unknown (null). Operator still picks.
 */
export function fitsOnSiteWindow(
  availableAt: string | Date | null | undefined,
  onSiteAt: string | Date | null | undefined,
): boolean | null {
  const available = listingDayKey(availableAt);
  const onSite = listingDayKey(onSiteAt);
  if (!available || !onSite) return null;
  return available <= onSite;
}
